import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { tableStyles } from "../../styles/tableStyles"
import { fetchFlotte } from "../../../store/actions/autreActions"
import VehiculeV2 from "./VehiculeV2"

const TYPE_LABEL = { 0: "Dépannage", 1: "Location", 2: "Autre" }
const STATUS_LABEL = { 0: "Disponible", 1: "Indisponible" }

const styles = {
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 16,
	},
	title: { fontSize: 15, fontWeight: 600, color: COLORS.text },
	count: { fontSize: 13, color: COLORS.textLight },
	empty: {
		...tableStyles.empty,
		padding: "40px 16px",
		color: COLORS.textLight,
		fontSize: 14,
	},
	typeChip: {
		display: "inline-flex",
		alignItems: "center",
		padding: "2px 9px",
		borderRadius: 99,
		fontSize: 11,
		fontWeight: 500,
		color: COLORS.accent,
		backgroundColor: COLORS.accentLight,
	},
}

const formatDate = (iso) => {
	if (!iso) return ""
	const [y, m, d] = iso.split("-")
	return `${d}.${m}.${y}`
}

const VehiculeRow = ({ veh, onDoubleClick, index }) => {
	const [hover, setHover] = useState(false)
	const pv = veh.propre_vehicule
	const sorti = pv && pv.sorti_le
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	const type = pv ? pv.type : null
	const dispo = veh.status === 0
	return (
		<tr
			onDoubleClick={onDoubleClick}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				cursor: "pointer",
				transition: "background-color 0.12s",
				opacity: sorti ? 0.5 : 1,
			}}
		>
			<td style={{ ...tableStyles.td, fontWeight: 500 }}>
				{veh.plaques || "—"}
			</td>
			<td style={tableStyles.td}>{veh.marque || "—"}</td>
			<td style={tableStyles.td}>{veh.modele || "—"}</td>
			<td style={tableStyles.td}>
				{type !== null ? (
					<span style={styles.typeChip}>{TYPE_LABEL[type]}</span>
				) : (
					"—"
				)}
			</td>
			<td style={tableStyles.td}>
				{sorti ? (
					<span style={gs.badge(COLORS.textMuted, COLORS.bg)}>
						Sorti le {formatDate(sorti)}
						{pv.raison_sortie ? ` — ${pv.raison_sortie}` : ""}
					</span>
				) : (
					<span
						style={gs.badge(
							dispo ? COLORS.success : COLORS.textMuted,
							dispo ? COLORS.successLight : COLORS.bg,
						)}
					>
						{STATUS_LABEL[veh.status] || "—"}
					</span>
				)}
			</td>
		</tr>
	)
}

const FlotteV2 = () => {
	const dispatch = useDispatch()
	const vehicules = useSelector((s) => s.masterReducer.masterVehicules)
	const [editing, setEditing] = useState(null)
	const [inclureSortis, setInclureSortis] = useState(false)
	const [filtreType, setFiltreType] = useState("")

	useEffect(() => {
		dispatch(fetchFlotte(inclureSortis))
	}, [dispatch, inclureSortis])

	const rows = useMemo(
		() =>
			[...(vehicules || [])]
				.filter((v) => {
					if (filtreType === "") return true
					const t = v.propre_vehicule ? v.propre_vehicule.type : null
					return t === parseInt(filtreType)
				})
				.sort((a, b) =>
					(a.plaques || "").localeCompare(b.plaques || "", "fr"),
				),
		[vehicules, filtreType],
	)

	return (
		<>
			{editing && (
				<VehiculeV2
					vehicule={editing}
					onClose={() => setEditing(null)}
					inclureSortis={inclureSortis}
				/>
			)}
			<div style={styles.header}>
				<div
					style={{
						display: "flex",
						alignItems: "baseline",
						gap: 10,
					}}
				>
					<span style={styles.title}>Flotte</span>
					<span style={styles.count}>{rows.length}</span>
				</div>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 16,
					}}
				>
					<select
						value={filtreType}
						onChange={(e) => setFiltreType(e.target.value)}
						style={{
							...gs.select,
							fontSize: 13,
							padding: "5px 10px",
							minWidth: 120,
						}}
					>
						<option value="">Tous</option>
						{Object.entries(TYPE_LABEL).map(([k, v]) => (
							<option key={k} value={k}>
								{v}
							</option>
						))}
					</select>
					<label
						style={{
							display: "flex",
							alignItems: "center",
							gap: 6,
							fontSize: 13,
							color: COLORS.textMuted,
							cursor: "pointer",
						}}
					>
						<input
							type="checkbox"
							checked={inclureSortis}
							onChange={(e) => setInclureSortis(e.target.checked)}
						/>
						Afficher les véhicules sortis
					</label>
					<button
						style={gs.btnPrimary}
						onClick={() => setEditing({})}
					>
						+ Nouveau véhicule
					</button>
				</div>
			</div>
			<div style={tableStyles.container}>
				{rows.length === 0 ? (
					<div style={styles.empty}>Aucun véhicule.</div>
				) : (
					<table style={tableStyles.table}>
						<thead>
							<tr>
								<th style={tableStyles.th}>Plaques</th>
								<th style={tableStyles.th}>Marque</th>
								<th style={tableStyles.th}>Modèle</th>
								<th style={tableStyles.th}>Type</th>
								<th style={tableStyles.th}>Statut</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((veh, i) => (
								<VehiculeRow
									key={veh.id ?? i}
									veh={veh}
									index={i}
									onDoubleClick={() => setEditing(veh)}
								/>
							))}
						</tbody>
					</table>
				)}
			</div>
		</>
	)
}

export default FlotteV2
